import React, { useEffect, useRef, useState } from 'react';
import { GET_COLLECTION_AGGREGATE_DATA } from '../../api';
import $ from 'jquery';
import { capitalize } from "../../utils/capitalize";
import cogoToast from "cogo-toast";
import moment from "moment/moment";
import { useSelector } from "react-redux";
import {opportunity} from "../../api/app";

let filter = {  };
// let filter = { type: "job" };

const searchableFields = [
    { field: 'description', isNumeric: false },
    { field: 'company.name', isNumeric: false },
    { field: 'position', isNumeric: false }
];

const Dashboard = () => {
    document.title = "Dashboard";

    const token = localStorage.getItem('jwtToken');
    const auth = useSelector((state) => state.auth.user);

    const formRef = useRef(null);
    const datatableRef = useRef(null);

    const [currentStep, setCurrentStep] = useState(1);
    const [errors, setErrors] = useState({});

    const [link, setLink] = useState("");
    const [useAI, setUseAI] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [opportunityName, setOpportunityName] = useState("");
    const [position, setPosition] = useState("");
    const [location, setLocation] = useState("");
    const [requiresDocs, setRequiresDocs] = useState(false);
    const [applicationType, setApplicationType] = useState("direct");
    const [opportunityType, setOpportunityType] = useState("job");
    const [checkedDocs, setCheckedDocs] = useState([]);
    const [editorModal, setEditorModal] = useState({ open: false, id: "", title: "" });
    const [editorValues, setEditorValues] = useState({ description: "", qualifications: "", experience: "" });
    const [questions, setQuestions] = useState([]);
    const [requiresQuestionnaire, setRequiresQuestionnaire] = useState(false);

    useEffect(() => {
        if (currentStep !== 4) return;

        const initDatePickers = () => {
            // Destroy existing instances first so they reinit on fresh DOM elements
            ['date_appear', 'date_closing', 'date_results'].forEach((id) => {
                const el = document.getElementById(id);
                if (!el) return;

                // Try all possible KTDatePicker namespaces
                const KTDatePicker =
                    window.KTDatePicker ||
                    window.KTDatepicker ||
                    window.KTUI?.KTDatePicker;

                if (!KTDatePicker) return;

                // Destroy existing instance if present
                try {
                    const existing = KTDatePicker.getInstance?.(el);
                    if (existing && typeof existing.destroy === 'function') {
                        existing.destroy();
                    }
                } catch {}

                // Remove initialized attribute so it gets picked up fresh
                el.removeAttribute('data-kt-date-picker-initialized');
                el.closest('[data-kt-date-picker]')?.removeAttribute('data-kt-date-picker-initialized');
            });

            // Now reinitialize all instances
            const KTDatePicker =
                window.KTDatePicker ||
                window.KTDatepicker ||
                window.KTUI?.KTDatePicker;

            if (KTDatePicker?.createInstances) {
                KTDatePicker.createInstances();
            }
        };

        setTimeout(initDatePickers, 50);
    }, [currentStep]);

    const clearFieldError = (field) => {
        setErrors(prev => {
            const next = { ...prev };
            delete next[field];
            return next;
        });
    };

    const getValue = (id) => document.getElementById(id)?.value?.trim() || "";

    const isValidUrl = (value) => {
        try {
            const url = new URL(value);
            return url.protocol === "http:" || url.protocol === "https:";
        } catch {
            return false;
        }
    };

    const validateStep = (step) => {
        const nextErrors = {};

        if (step === 1) {
            if (!opportunityType) nextErrors.opportunity_type = "Please select an opportunity type.";
        }

        if (step === 2) {
            if (!opportunityName.trim()) nextErrors.opportunity_name = "Opportunity name is required.";

            if (opportunityType === "job" && !position.trim()) {
                nextErrors.position = "Position is required for job opportunities.";
            }

            if (applicationType === "external") {
                if (!link.trim()) nextErrors.link = "Application link is required.";
                else if (!isValidUrl(link)) nextErrors.link = "Enter a valid URL, e.g. https://example.com";
            }

            if (applicationType === "external") {
                const link = getValue("link");
                if (!link) nextErrors.link = "Application link is required.";
                else if (!isValidUrl(link)) nextErrors.link = "Enter a valid URL, e.g. https://example.com";
            }

            if (!editorValues.description.trim()) nextErrors.description = "Description is required.";
        }

        if (step === 3) {
            if (requiresQuestionnaire) {
                const filled = questions.filter(q => q.trim());
                if (filled.length === 0) nextErrors.questions = "Add at least one question.";
            }

            if (requiresDocs) {
                const otherDocs = getValue("doc_other");
                if (checkedDocs.length === 0 && !otherDocs) {
                    nextErrors.documents = "Please select or enter at least one required document.";
                }
            }
        }

        if (step === 4) {
            if (!getValue("date_closing")) nextErrors.date_closing = "Closing date is required.";
            if (!document.getElementById("privacy_policy")?.checked) {
                nextErrors.privacy_policy = "You must accept the data privacy policy.";
            }
        }

        return nextErrors;
    };

    const getPrevStep = (step) => {
        if (step === 4 && applicationType === "external") return 2;
        return Math.max(step - 1, 1);
    };

    const goNext = () => {
        // Only validate steps 1, 2, and 3 when clicking Next
        if (currentStep < 4) {
            const nextErrors = validateStep(currentStep);
            if (Object.keys(nextErrors).length > 0) {
                setErrors(nextErrors);
                cogoToast.error("Please fix the highlighted fields.");
                return;
            }
        }

        setErrors({});
        setCurrentStep(prev => {
            if (prev === 2 && applicationType === "external") return 4;
            return Math.min(prev + 1, 4);
        });
    };

    const goBack = () => {
        setErrors({});
        setCurrentStep(prev => getPrevStep(prev));
    };

    const handleSubmit = (e) => {
        e?.preventDefault?.();
        if (currentStep !== 4) return;
        const nextErrors = validateStep(4);
        if (Object.keys(nextErrors).length > 0) {
            setErrors(nextErrors);
            cogoToast.error("Please complete all required fields.");
            return;
        }

        setErrors({});
        setIsSubmitting(true);

        const otherDocs = getValue("doc_other")
            ?.split(',')
            .map(item => item.trim())
            .filter(Boolean) || [];

        const toISO = (val) => {
            if (!val) return null;
            const parsed = new Date(val);
            return isNaN(parsed.getTime()) ? null : parsed.toISOString();
        };

        const data = {
            type: opportunityType,
            opportunity_name: opportunityName,
            position: opportunityType === "job" ? position : "",
            link: applicationType === "external" ? link : "",
            location: location,
            company: auth?.user.company,
            description: editorValues.description,
            qualifications: editorValues.qualifications,
            experience: editorValues.experience,
            ...(requiresDocs && {
                documents: [
                    ...checkedDocs.map(label => ({ field: label })),
                    ...otherDocs.map(label => ({ field: label }))
                ]
            }),
            ...(requiresQuestionnaire && {
                questionnaires: questions.filter(q => q.trim()).map(q => ({ field: q }))
            }),
            appearanceDate: toISO(getValue("date_appear")),
            closingDate: toISO(getValue("date_closing")),
            resultDate: toISO(getValue("date_results")),
            useAI: applicationType !== "external" ? useAI : false
        };

        opportunity("post", data).then((result) => {
            setIsSubmitting(false);
            if(!result.error) {
                cogoToast.success("Data successfully created.", {position: "top-right", hideAfter: 5});
                resetForm("create-modal");
                reloadTable();
            }
        });
    };

    const handleAddQuestion = () => {
        if (questions.length < 5) setQuestions(prev => [...prev, '']);
    };

    const handleRemoveQuestion = (index) => {
        setQuestions(prev => prev.filter((_, i) => i !== index));
    };

    const handleQuestionChange = (index, value) => {
        clearFieldError("questions");
        setQuestions(prev => prev.map((q, i) => i === index ? value : q));
    };

    const handleOpenEditor = (id, title) => setEditorModal({ open: true, id, title });
    const handleCloseEditor = () => setEditorModal({ open: false, id: "", title: "" });

    // ─── Datatable ────────────────────────────────────────────────────────────
    useEffect(() => {
        window.deleteOpportunity = (id) => {
            deleteOpportunityRef.current?.(id);
        };

        var KTDatatableRemoteDataDemo = (function () {
            var isInitialized = false;
            var instance = null;

            var resolveDataTableClass = function () {
                if (typeof window === 'undefined') return null;
                if (window.KTDataTable) return window.KTDataTable;
                if (window.KTUI && window.KTUI.KTDataTable) return window.KTUI.KTDataTable;
                return null;
            };

            var init = function () {
                var KTDataTable = resolveDataTableClass();
                if (!KTDataTable) { setTimeout(init, 100); return null; }
                if (isInitialized && instance) return instance;

                var datatableEl = document.getElementById('kt_datatable_remote_source');
                if (!datatableEl) return null;

                if (datatableEl.hasAttribute('data-kt-datatable-initialized')) {
                    if (typeof KTDataTable.getInstance === 'function') {
                        var oldInstance = KTDataTable.getInstance(datatableEl);
                        if (oldInstance && typeof oldInstance.dispose === 'function') oldInstance.dispose();
                    }
                    datatableEl.removeAttribute('data-kt-datatable-initialized');
                    if (datatableEl.instance) delete datatableEl.instance;
                }

                var datatable = new KTDataTable(datatableEl, {
                    apiEndpoint: GET_COLLECTION_AGGREGATE_DATA,
                    requestMethod: 'POST',
                    requestHeaders: {
                        Authorization: token,
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                    },
                    mapRequest: function (queryParams) {
                        const pageSize = datatable?.getState?.().pageSize || datatable?.getState?.().size || Number(datatableEl.getAttribute('data-kt-datatable-page-size')) || 10;
                        const pageIndex = datatable?.getState?.().page || datatable?.getState?.().pageIndex || 1;
                        return JSON.stringify({
                            query: queryParams.search || queryParams.query || '',
                            collection: 'opportunity',
                            searchableFields,
                            filter,
                            pageSize: 0,
                            pageIndex,
                            format: 'json',
                            populate: [
                                { path: "company", model: "company" },
                                { path: "admin_level.provincial", model: "administrative" },
                                { path: "admin_level.district", model: "district" },
                                { path: "admin_level.constituency", model: "constituency" }
                            ],
                            sort: { _id: -1 }
                        });
                    },
                    mapResponse: function (res) {
                        return {
                            data: res.data || [],
                            totalCount: res.total || 0,
                            page: res.page || 1,
                            pageSize: res.pageSize || 10,
                            totalPages: Math.ceil((res.total || 0) / (res.pageSize || 10)),
                        };
                    },
                    columns: {
                        position: {
                            title: 'Position',
                            render: value => value ? `${value}`.trim() : '—',
                        },
                        location: {
                            title: 'Location',
                            render: value => value ?? '—',
                        },
                        status: {
                            title: 'Status',
                            render: value => `<span class="kt-badge">${capitalize(value ?? 'pending')}</span>`,
                        },
                        createdAt: {
                            title: 'Date',
                            render: value => value ? moment(value).format('DD MMM YYYY') : '—',
                        },
                        _id: {
                            title: 'Action',
                            render: value => `
                                <div class="kt-menu" data-kt-menu="true">
                                    <div class="kt-menu-item" data-kt-menu-item-offset="0, 10px" data-kt-menu-item-placement="bottom-start" data-kt-menu-item-toggle="dropdown" data-kt-menu-item-trigger="click">
                                        <button class="kt-menu-toggle kt-btn kt-btn-sm kt-btn-icon kt-btn-outline">
                                            <i class="ki-filled ki-dots-vertical text-lg"></i>
                                        </button>
                                        <div class="kt-menu-dropdown kt-menu-default w-full max-w-[200px]" data-kt-menu-dismiss="true">
                                            <div class="kt-menu-item">
                                                <a class="kt-menu-link" href="#/opportunity/${value}">
                                                    <span class="kt-menu-icon"><i class="ki-filled ki-users"></i></span>
                                                    <span class="kt-menu-title">Applicants</span>
                                                </a>
                                            </div>
                                            <div class="kt-menu-item">
                                                <a class="kt-menu-link delete_opportunity" href="javascript:void(0)" onclick="event.preventDefault(); window.deleteOpportunity('${value}')">
                                                    <span class="kt-menu-icon"><i class="ki-filled ki-trash"></i></span>
                                                    <span class="kt-menu-title">Delete</span>
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            `
                        }
                    },
                    pageSize: 10,
                    stateSave: true,
                });

                isInitialized = true;
                instance = datatable;
                datatableRef.current = datatable;
                return instance;
            };

            return { init };
        })();

        function safeInitialize() {
            var element = document.getElementById('kt_datatable_remote_source');
            if (!element) return;
            var inst = KTDatatableRemoteDataDemo.init();
            if (inst) window.datatableInstance = inst;
        }

        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', safeInitialize, { once: true });
        } else {
            setTimeout(safeInitialize, 1);
        }

        return () => {
            delete window.deleteOpportunity;
        };

    }, [token]);

    const deleteOpportunity = (id) => {
        const options = {
            position: "top-right",
            hideAfter: 0
        };

        let {hide} = cogoToast.loading('Please wait... contacting to server.', options);

        let data = {
            id
        }

        opportunity("delete", data).then((result) => {
            hide();
            if(!result.error) {
                reloadTable();
                cogoToast.success("Opportunity successfully removed.", {position: "top-right"});
            }
        });
    }

    const deleteOpportunityRef = useRef(null);
    deleteOpportunityRef.current = deleteOpportunity;

    const reloadTable = () => {
        if (datatableRef.current && typeof datatableRef.current.reload === 'function') {
            datatableRef.current.reload();
        }
    };

    const handleShowModal = () => {
        const element = $("#create-modal")[0];
        if (!element) return;
        let modal = window.KTModal.getInstance(element);
        if (!modal) modal = new window.KTModal(element);
        modal.show();
    };

    const resetForm = (id) => {
        formRef.current?.reset();
        setCurrentStep(1);
        setErrors({});
        setEditorValues({ description: "", qualifications: "", experience: "" });
        setQuestions([]);
        setRequiresQuestionnaire(false);
        setRequiresDocs(false);
        setCheckedDocs([]);
        setUseAI(false);
        setApplicationType("direct");
        setOpportunityType("job");
        setOpportunityName("");
        setPosition("");
        setLocation("");
        setLink("");

        const element = $(`#${id}`)[0];
        if (!element) return;
        let modal = window.KTModal.getInstance(element);
        if (!modal) modal = new window.KTModal(element);
        modal.hide();
    };

    const visibleSteps = applicationType === "external"
        ? [{ label: "Type", step: 1 }, { label: "Basic Info", step: 2 }, { label: "Settings", step: 4 }]
        : [{ label: "Type", step: 1 }, { label: "Basic Info", step: 2 }, { label: "Requirement", step: 3 }, { label: "Settings", step: 4 }];

    const docOptions = [
        { id: "doc_matric", label: "Matric Certificate", description: "Grade 12 certificate or equivalent." },
        { id: "doc_id_copy", label: "ID Copy", description: "Certified copy of national ID." },
        { id: "doc_portfolio", label: "Portfolio", description: "Work samples or portfolio documents." },
        { id: "doc_transcript", label: "Academic Transcript", description: "Official academic records." },
        { id: "doc_reference", label: "Reference Letter", description: "Professional or academic reference." },
        { id: "doc_certificate", label: "Certificates", description: "Any relevant certificates." },
    ];

    return (
        <div>
            {/* Page Header */}
            <div className="kt-container-fixed">
                <div className="flex flex-wrap items-center lg:items-end justify-between gap-5 pb-7.5">
                    <div className="flex flex-col justify-center gap-2">
                        <h1 className="text-xl font-medium leading-none text-mono">Dashboard</h1>
                        <div className="flex items-center gap-2 text-sm font-normal text-secondary-foreground">
                            Central Hub for Personal Customization
                        </div>
                    </div>
                    <button type="button" className="kt-btn kt-btn-destructive" onClick={handleShowModal}>
                        <i className="ki-filled ki-plus"></i>
                        Create Opportunity
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="kt-container-fixed">
                <div className="kt-card mb-10">
                    <div className="kt-card-header">
                        <h3 className="kt-card-title">Opportunity</h3>
                        <div className="kt-input max-w-48">
                            <i className="ki-filled ki-magnifier"></i>
                            <input type="text" placeholder="Search..." className="kt-input sm:w-48" data-kt-datatable-search="#kt_datatable_remote_source" />
                        </div>
                    </div>
                    <div id="kt_datatable_remote_source" className="kt-card-table" data-kt-datatable-page-size="10" data-kt-datatable-state-save="true">
                        <div className="kt-table-wrapper kt-scrollable">
                            <table className="kt-table" data-kt-datatable-table="true">
                                <thead>
                                <tr>
                                    <th scope="col" className="w-30" data-kt-datatable-column="position"><span className="kt-table-col"><span className="kt-table-col-label">Position</span><span className="kt-table-col-sort"></span></span></th>
                                    <th scope="col" className="w-30" data-kt-datatable-column="location"><span className="kt-table-col"><span className="kt-table-col-label">Location</span><span className="kt-table-col-sort"></span></span></th>
                                    <th scope="col" className="w-30" data-kt-datatable-column="status"><span className="kt-table-col"><span className="kt-table-col-label">Status</span><span className="kt-table-col-sort"></span></span></th>
                                    <th scope="col" className="w-30" data-kt-datatable-column="createdAt"><span className="kt-table-col"><span className="kt-table-col-label">Date</span><span className="kt-table-col-sort"></span></span></th>
                                    <th scope="col" className="w-24" data-kt-datatable-column="_id"><span className="kt-table-col"><span className="kt-table-col-label">Action</span></span></th>
                                </tr>
                                </thead>
                                <tbody></tbody>
                            </table>
                        </div>
                        <div className="kt-datatable-toolbar">
                            <div className="kt-datatable-length">Show <select className="kt-select kt-select-sm w-16" name="perpage" data-kt-datatable-size="true"></select> per page</div>
                            <div className="kt-datatable-info">
                                <span data-kt-datatable-info="true"></span>
                                <div className="kt-datatable-pagination" data-kt-datatable-pagination="true"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal */}
            <div className="kt-modal" data-kt-modal="true" data-kt-modal-backdrop-static="true" id="create-modal">
                <div className="kt-modal-content max-w-[980px] top-[2%]">
                    <div className="kt-modal-header">
                        <div>
                            <h3 className="kt-modal-title">Create Opportunity</h3>
                            <p className="text-sm font-normal text-secondary-foreground">Add opportunity to the system.</p>
                        </div>
                        <button type="button" className="kt-modal-close" onClick={() => resetForm("create-modal")}>
                            <i className="ki-filled ki-cross text-lg"></i>
                        </button>
                    </div>

                    <div className="kt-modal-body p-0">
                        <form ref={formRef} className="kt-form" noValidate onSubmit={handleSubmit}>
                            <div>
                                {/* ── Stepper Header ── */}
                                <div className="kt-card-header h-auto px-10 py-5">
                                    {visibleSteps.map(({ label, step }, index) => {
                                        const isActive = currentStep === step;
                                        const isCompleted = currentStep > step;
                                        return (
                                            <div key={step} className="flex gap-2.5 items-center">
                                                <div className={`shrink-0 rounded-full size-8 flex items-center justify-center text-sm font-semibold transition-colors ${
                                                    isActive ? "bg-primary text-primary-foreground"
                                                        : isCompleted ? "bg-green-500 text-white"
                                                            : "bg-muted text-muted-foreground"
                                                }`}>
                                                    {isCompleted
                                                        ? <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"></path></svg>
                                                        : index + 1
                                                    }
                                                </div>
                                                <div className="flex flex-col gap-0.5">
                                                    <h4 className="text-sm font-medium text-mono">{label}</h4>
                                                    <span className="text-sm text-muted-foreground">Step {index + 1}</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* ── Step Content ── */}
                                <div className="kt-card-content px-5">

                                    {/* ── Step 1: Type ── */}
                                    {currentStep === 1 && (
                                        <div>
                                            <div className="flex flex-col gap-2 mb-6">
                                                <h3 className="text-base font-medium text-mono">Opportunity Type</h3>
                                                <p className="text-sm text-muted-foreground">Choose the type of opportunity you are creating.</p>
                                            </div>
                                            <div className="grid grid-cols-2 gap-3">
                                                {[
                                                    ["job", "Job", "Full-time or part-time employment opportunities."],
                                                    ["apprenticeship", "Apprenticeship", "Earn while you learn on-the-job training programs."],
                                                    ["internship", "Internship", "Short-term work experience for students or graduates."],
                                                    ["training", "Training", "Skills development and professional training programs."],
                                                    ["tickets", "Tickets", "Event tickets, workshops or conference access."],
                                                    ["scholarship", "Scholarship", "Financial aid for educational pursuits."],
                                                    ["funding", "Funding", "Grants or funding for projects and businesses."]
                                                ].map(([value, title, description]) => (
                                                    <label key={value} htmlFor={`type_${value}`}
                                                           className={`relative flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all hover:border-primary hover:bg-primary/5 has-[:checked]:border-primary has-[:checked]:bg-primary/5 ${errors.opportunity_type ? 'border-destructive' : 'border-border'}`}
                                                    >
                                                        <input
                                                            type="radio"
                                                            id={`type_${value}`}
                                                            name="opportunity_type"
                                                            value={value}
                                                            className="mt-0.5 kt-radio"
                                                            checked={opportunityType === value}
                                                            onChange={() => {
                                                                setOpportunityType(value);
                                                                clearFieldError("opportunity_type");
                                                            }}
                                                        />
                                                        <div className="flex flex-col gap-0.5">
                                                            <span className="text-sm font-medium text-mono">{title}</span>
                                                            <span className="text-xs text-muted-foreground">{description}</span>
                                                        </div>
                                                    </label>
                                                ))}
                                            </div>
                                            {errors.opportunity_type && <div className="kt-form-message mt-2">{errors.opportunity_type}</div>}
                                        </div>
                                    )}

                                    {/* ── Step 2: Basic Info ── */}
                                    {currentStep === 2 && (
                                        <div>
                                            <div className="flex flex-col gap-2 mb-6">
                                                <h3 className="text-base font-medium text-mono">Basic Information</h3>
                                                <p className="text-sm text-muted-foreground">Fill in the details for this opportunity.</p>
                                            </div>
                                            <div className="flex flex-col gap-5">

                                                {/* Application Type */}
                                                <div className="kt-form-item">
                                                    <label className="kt-form-label">Application Type <span className="text-destructive">*</span></label>
                                                    <div className="kt-form-control">
                                                        <div className="grid grid-cols-2 gap-3">
                                                            <label htmlFor="app_type_direct" className="relative flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all border-border hover:border-primary hover:bg-primary/5 has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                                                                <input
                                                                    type="radio"
                                                                    id="app_type_direct"
                                                                    name="application_type"
                                                                    value="direct"
                                                                    className="mt-0.5 kt-radio"
                                                                    checked={applicationType === "direct"}
                                                                    onChange={() => { setApplicationType("direct"); clearFieldError("link"); }}
                                                                />
                                                                <div className="flex flex-col gap-0.5">
                                                                    <span className="text-sm font-medium text-mono">Directly through the Imisepela App</span>
                                                                    <span className="text-xs text-muted-foreground">You have full access to applications through this dashboard.</span>
                                                                </div>
                                                            </label>
                                                            <label htmlFor="app_type_external" className="relative flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all border-border hover:border-primary hover:bg-primary/5 has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                                                                <input
                                                                    type="radio"
                                                                    id="app_type_external"
                                                                    name="application_type"
                                                                    value="external"
                                                                    className="mt-0.5 kt-radio"
                                                                    checked={applicationType === "external"}
                                                                    onChange={() => setApplicationType("external")}
                                                                />
                                                                <div className="flex flex-col gap-0.5">
                                                                    <span className="text-sm font-medium text-mono">External Link (Not Recommended)</span>
                                                                    <span className="text-xs text-muted-foreground">Applicants are redirected to an external website to apply.</span>
                                                                </div>
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Name of Opportunity — always shown */}
                                                <div className="kt-form-item">
                                                    <label className="kt-form-label">Name of opportunity <span className="text-destructive">*</span></label>
                                                    <div className="kt-form-control">
                                                        <input
                                                            id="opportunity_name"
                                                            type="text"
                                                            className="kt-input"
                                                            placeholder="Name this opportunity (eg. Empowerment Grant, Small Business Loan)"
                                                            autoComplete="off"
                                                            value={opportunityName}
                                                            aria-invalid={errors.opportunity_name ? "true" : undefined}
                                                            onChange={(e) => {
                                                                setOpportunityName(e.target.value);
                                                                clearFieldError("opportunity_name");
                                                            }}
                                                        />
                                                    </div>
                                                    {errors.opportunity_name
                                                        ? <div className="kt-form-message">{errors.opportunity_name}</div>
                                                        : <div className="kt-form-description">This is the title applicants will see.</div>
                                                    }
                                                </div>

                                                {/* Position — job type only */}
                                                {opportunityType === "job" && (
                                                    <div className="kt-form-item">
                                                        <label className="kt-form-label">Position <span className="text-destructive">*</span></label>
                                                        <div className="kt-form-control">
                                                            <input
                                                                id="position"
                                                                type="text"
                                                                className="kt-input"
                                                                placeholder="Enter job title (eg. Waiter, Receptionist, Apprentice Plumber)"
                                                                autoComplete="off"
                                                                value={position}
                                                                aria-invalid={errors.position ? "true" : undefined}
                                                                onChange={(e) => {
                                                                    setPosition(e.target.value);
                                                                    clearFieldError("position");
                                                                }}
                                                            />
                                                        </div>
                                                        {errors.position
                                                            ? <div className="kt-form-message">{errors.position}</div>
                                                            : <div className="kt-form-description">The specific role or position being offered.</div>
                                                        }
                                                    </div>
                                                )}

                                                {/* External Link */}
                                                <div className="kt-form-item">
                                                    <label className={`kt-form-label ${applicationType === "direct" ? "opacity-40" : ""}`}>
                                                        Link to Application {applicationType === "external" && <span className="text-destructive">*</span>}
                                                    </label>
                                                    <div className="kt-form-control">
                                                        <input
                                                            id="link"
                                                            type="url"
                                                            className={`kt-input ${applicationType === "direct" ? "opacity-40 cursor-not-allowed" : ""}`}
                                                            placeholder="https://..."
                                                            autoComplete="off"
                                                            disabled={applicationType === "direct"}
                                                            value={link}
                                                            aria-invalid={errors.link ? "true" : undefined}
                                                            onChange={(e) => {
                                                                setLink(e.target.value);
                                                                clearFieldError("link");
                                                            }}
                                                        />
                                                    </div>
                                                    {errors.link
                                                        ? <div className="kt-form-message">{errors.link}</div>
                                                        : applicationType === "direct"
                                                            ? <div className="kt-form-description">Not required — applicants will apply directly through the app.</div>
                                                            : <div className="kt-form-description">Enter the full URL where applicants should apply.</div>
                                                    }
                                                </div>

                                                {/* Location */}
                                                <div className="kt-form-item">
                                                    <label className="kt-form-label">Location</label>
                                                    <div className="kt-form-control">
                                                        <input
                                                            id="location"
                                                            type="text"
                                                            className="kt-input"
                                                            placeholder="Enter location"
                                                            autoComplete="off"
                                                            value={location}
                                                            onChange={(e) => setLocation(e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="kt-form-description">City, province or remote.</div>
                                                </div>

                                                {/* Description, Qualifications, Experience */}
                                                <div className="kt-form-item">
                                                    <div className="grid grid-cols-3 gap-3">
                                                        {[
                                                            { id: "description", title: "Describe this opportunity", icon: "ki-filled ki-document", required: true },
                                                            { id: "qualifications", title: "What qualifications are required?", icon: "ki-filled ki-book", required: false },
                                                            { id: "experience", title: "What experience is required?", icon: "ki-filled ki-briefcase", required: false },
                                                        ].map((item) => (
                                                            <div
                                                                key={item.id}
                                                                onClick={() => { handleOpenEditor(item.id, item.title); clearFieldError(item.id); }}
                                                                className={`flex flex-col gap-2 p-4 rounded-lg border cursor-pointer transition-all hover:border-primary hover:bg-primary/5 ${errors[item.id] ? 'border-destructive' : 'border-border'}`}
                                                            >
                                                                <div className="flex items-center justify-between">
                                                                    <div className="flex items-center gap-2">
                                                                        <i className={`${item.icon} text-lg text-muted-foreground`}></i>
                                                                        <span className="text-sm font-medium text-mono">
                                                                            {item.title}{item.required && <span className="text-destructive ml-1">*</span>}
                                                                        </span>
                                                                    </div>
                                                                    <i className="ki-filled ki-pencil text-sm text-muted-foreground"></i>
                                                                </div>
                                                                <p className="text-xs text-muted-foreground line-clamp-2">
                                                                    {editorValues[item.id] ? editorValues[item.id].replace(/<[^>]+>/g, '') : `Click to add ${item.title.toLowerCase()}...`}
                                                                </p>
                                                                {editorValues[item.id] && <span className="text-xs text-primary font-medium">● Filled</span>}
                                                            </div>
                                                        ))}
                                                    </div>
                                                    {errors.description && <div className="kt-form-message mt-2">{errors.description}</div>}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* ── Step 3: Requirements ── */}
                                    {currentStep === 3 && (
                                        <div>
                                            <div className="flex flex-col gap-2 mb-6">
                                                <h3 className="text-base font-medium text-mono">Requirements</h3>
                                                <p className="text-sm text-muted-foreground">Define what information and documents are required from applicants.</p>
                                            </div>
                                            <div className="flex flex-col gap-5">

                                                {/* Information Required */}
                                                <div className="flex flex-col gap-3">
                                                    <div className="flex flex-col gap-1">
                                                        <label className="kt-form-label font-normal text-mono">What information do you need from the applicants?</label>
                                                        <p className="text-sm text-muted-foreground">Select the information applicants must provide.</p>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-3">
                                                        {[
                                                            { id: "req_name", label: "Full Name", description: "Applicant's full legal name." },
                                                            { id: "req_province", label: "Province", description: "Province where the applicant resides." },
                                                            { id: "req_cv", label: "CV / Resume", description: "Uploaded curriculum vitae or resume." },
                                                            { id: "req_contact", label: "Contact Number", description: "Applicant's phone or mobile number." },
                                                            { id: "req_email", label: "Email Address", description: "Applicant's active email address." },
                                                            { id: "req_id", label: "ID Number", description: "National identification number." },
                                                            { id: "req_dob", label: "Date of Birth", description: "Applicant's date of birth." },
                                                            { id: "req_gender", label: "Gender", description: "Applicant's gender identity." },
                                                        ].map((item) => (
                                                            <label
                                                                key={item.id}
                                                                htmlFor={item.id}
                                                                className="relative flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all border-border hover:border-primary hover:bg-primary/5 has-[:checked]:border-primary has-[:checked]:bg-primary/5"
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    id={item.id}
                                                                    className="mt-0.5 kt-checkbox"
                                                                />
                                                                <div className="flex flex-col gap-0.5">
                                                                    <span className="text-sm font-medium text-mono">{item.label}</span>
                                                                    <span className="text-xs text-muted-foreground">{item.description}</span>
                                                                </div>
                                                            </label>
                                                        ))}
                                                    </div>
                                                </div>

                                                {/* Questionnaire */}
                                                <div className="kt-form-item">
                                                    <label className="kt-form-label">Do you have any questions for applicants?</label>
                                                    <p className="text-sm text-muted-foreground mb-3">Would you like applicants to answer custom questions as part of their application?</p>
                                                    <div className="kt-form-control">
                                                        <div className="grid grid-cols-2 gap-3">
                                                            <label htmlFor="questionnaire_no" className="relative flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all border-border hover:border-primary hover:bg-primary/5 has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                                                                <input
                                                                    type="radio"
                                                                    id="questionnaire_no"
                                                                    name="requires_questionnaire"
                                                                    value="no"
                                                                    className="mt-0.5 kt-radio"
                                                                    checked={!requiresQuestionnaire}
                                                                    onChange={() => { setRequiresQuestionnaire(false); setQuestions([]); setErrors(p => { const n = { ...p }; delete n.questions; return n; }); }}
                                                                />
                                                                <div className="flex flex-col gap-0.5">
                                                                    <span className="text-sm font-medium text-mono">No</span>
                                                                    <span className="text-xs text-muted-foreground">No questions needed from applicants.</span>
                                                                </div>
                                                            </label>
                                                            <label htmlFor="questionnaire_yes" className="relative flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all border-border hover:border-primary hover:bg-primary/5 has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                                                                <input
                                                                    type="radio"
                                                                    id="questionnaire_yes"
                                                                    name="requires_questionnaire"
                                                                    value="yes"
                                                                    className="mt-0.5 kt-radio"
                                                                    checked={requiresQuestionnaire}
                                                                    onChange={() => setRequiresQuestionnaire(true)}
                                                                />
                                                                <div className="flex flex-col gap-0.5">
                                                                    <span className="text-sm font-medium text-mono">Yes</span>
                                                                    <span className="text-xs text-muted-foreground">Applicants will answer your custom questions.</span>
                                                                </div>
                                                            </label>
                                                        </div>
                                                    </div>

                                                    {requiresQuestionnaire && (
                                                        <div className="flex flex-col gap-2 mt-3">
                                                            <label className="kt-form-label">Your Questions</label>
                                                            <p className="text-sm text-muted-foreground">Add up to 5 questions for applicants to answer.</p>
                                                            <div className="flex flex-col gap-2 mt-1">
                                                                {questions.map((q, index) => (
                                                                    <div key={index} className="flex items-center gap-2">
                                                                        <span className="text-xs text-muted-foreground w-4 shrink-0">{index + 1}.</span>
                                                                        <input
                                                                            type="text"
                                                                            className="kt-input flex-1"
                                                                            placeholder={`Enter question ${index + 1}...`}
                                                                            value={q}
                                                                            aria-invalid={errors.questions && q.trim() === '' ? "true" : undefined}
                                                                            onChange={(e) => handleQuestionChange(index, e.target.value)}
                                                                        />
                                                                        <button type="button" onClick={() => handleRemoveQuestion(index)} className="shrink-0 size-8 flex items-center justify-center rounded-lg border border-border text-muted-foreground hover:border-destructive hover:text-destructive transition-all">
                                                                            <i className="ki-filled ki-trash text-sm"></i>
                                                                        </button>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                            {questions.length < 5 && (
                                                                <button type="button" onClick={handleAddQuestion} className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-dashed border-border hover:border-primary hover:bg-primary/5 text-sm text-muted-foreground hover:text-primary transition-all w-full">
                                                                    <i className="ki-filled ki-plus text-sm"></i>
                                                                    Add Question {questions.length > 0 ? `(${questions.length}/5)` : ''}
                                                                </button>
                                                            )}
                                                            {questions.length === 5 && <p className="text-xs text-muted-foreground">Maximum of 5 questions reached.</p>}
                                                        </div>
                                                    )}
                                                    {errors.questions && <div className="kt-form-message mt-2">{errors.questions}</div>}
                                                </div>

                                                {/* Documents */}
                                                <div className="kt-form-item pt-4 border-t border-border">
                                                    <label className="kt-form-label">Are any additional documents needed?</label>
                                                    <p className="text-sm text-muted-foreground mb-3">Do you need applicants to upload additional documents?</p>
                                                    <div className="kt-form-control">
                                                        <div className="grid grid-cols-2 gap-3">
                                                            <label htmlFor="docs_no" className="relative flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all border-border hover:border-primary hover:bg-primary/5 has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                                                                <input
                                                                    type="radio"
                                                                    id="docs_no"
                                                                    name="additional_docs"
                                                                    value="no"
                                                                    className="mt-0.5 kt-radio"
                                                                    checked={!requiresDocs}
                                                                    onChange={() => { setRequiresDocs(false); setCheckedDocs([]); setErrors(p => { const n = { ...p }; delete n.documents; return n; }); }}
                                                                />
                                                                <div className="flex flex-col gap-0.5">
                                                                    <span className="text-sm font-medium text-mono">No</span>
                                                                    <span className="text-xs text-muted-foreground">No additional documents needed.</span>
                                                                </div>
                                                            </label>
                                                            <label htmlFor="docs_yes" className="relative flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all border-border hover:border-primary hover:bg-primary/5 has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                                                                <input
                                                                    type="radio"
                                                                    id="docs_yes"
                                                                    name="additional_docs"
                                                                    value="yes"
                                                                    className="mt-0.5 kt-radio"
                                                                    checked={requiresDocs}
                                                                    onChange={() => setRequiresDocs(true)}
                                                                />
                                                                <div className="flex flex-col gap-0.5">
                                                                    <span className="text-sm font-medium text-mono">Yes</span>
                                                                    <span className="text-xs text-muted-foreground">Applicants must upload specific documents.</span>
                                                                </div>
                                                            </label>
                                                        </div>
                                                    </div>

                                                    {requiresDocs && (
                                                        <div className="flex flex-col gap-3 mt-3">
                                                            <label className="kt-form-label">Document Name(s) Required</label>
                                                            <p className="text-sm text-muted-foreground">Select the documents applicants must upload.</p>
                                                            <div className="grid grid-cols-2 gap-3">
                                                                {docOptions.map((item) => (
                                                                    <label key={item.id} htmlFor={item.id}
                                                                           className={`relative flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all hover:border-primary hover:bg-primary/5 has-[:checked]:border-primary has-[:checked]:bg-primary/5 ${errors.documents ? 'border-destructive' : 'border-border'}`}
                                                                    >
                                                                        <input
                                                                            type="checkbox"
                                                                            id={item.id}
                                                                            className="mt-0.5 kt-checkbox"
                                                                            checked={checkedDocs.includes(item.label)}
                                                                            onChange={(e) => {
                                                                                clearFieldError("documents");
                                                                                setCheckedDocs(prev =>
                                                                                    e.target.checked
                                                                                        ? [...prev, item.label]
                                                                                        : prev.filter(d => d !== item.label)
                                                                                );
                                                                            }}
                                                                        />
                                                                        <div className="flex flex-col gap-0.5">
                                                                            <span className="text-sm font-medium text-mono">{item.label}</span>
                                                                            <span className="text-xs text-muted-foreground">{item.description}</span>
                                                                        </div>
                                                                    </label>
                                                                ))}
                                                            </div>
                                                            <div className="kt-form-item mt-1">
                                                                <label className="kt-form-label">Other Documents</label>
                                                                <div className="kt-form-control">
                                                                    <input
                                                                        id="doc_other"
                                                                        type="text"
                                                                        className="kt-input"
                                                                        placeholder="e.g. Driver's licence, Teaching qualification etc..."
                                                                        autoComplete="off"
                                                                        onChange={() => clearFieldError("documents")}
                                                                    />
                                                                </div>
                                                                <div className="kt-form-description">Separate multiple documents with a comma.</div>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {errors.documents && <div className="kt-form-message mt-2">{errors.documents}</div>}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* ── Step 4: Settings ── */}
                                    {currentStep === 4 && (
                                        <div>
                                            <div className="flex flex-col gap-2 mb-6">
                                                <h3 className="text-base font-medium text-mono">When can applicants apply?</h3>
                                                <p className="text-sm text-muted-foreground">Select the dates for your application process.</p>
                                            </div>
                                            <div className="flex flex-col gap-5">

                                                {/* Dates */}
                                                <div className="grid grid-cols-3 gap-3">
                                                    <div className="kt-form-item">
                                                        <label className="kt-form-label">Appearance Date</label>
                                                        <div className="kt-form-control">
                                                            <div className="kt-input">
                                                                <i className="ki-outline ki-calendar"></i>
                                                                <input id="date_appear" className="grow" type="text" placeholder="Select date" data-kt-date-picker="true" data-kt-date-picker-input-mode="true" data-kt-date-picker-position-to-input="left" readOnly />
                                                            </div>
                                                        </div>
                                                        <div className="kt-form-description">When should this appear on the app?</div>
                                                    </div>
                                                    <div className="kt-form-item">
                                                        <label className="kt-form-label">Closing Date <span className="text-destructive">*</span></label>
                                                        <div className="kt-form-control">
                                                            <div className={`kt-input ${errors.date_closing ? 'border-destructive' : ''}`}>
                                                                <i className="ki-outline ki-calendar"></i>
                                                                <input
                                                                    id="date_closing"
                                                                    className="grow"
                                                                    type="text"
                                                                    placeholder="Select date"
                                                                    data-kt-date-picker="true"
                                                                    data-kt-date-picker-input-mode="true"
                                                                    data-kt-date-picker-position-to-input="left"
                                                                    readOnly
                                                                    aria-invalid={errors.date_closing ? "true" : undefined}
                                                                    onChange={() => clearFieldError("date_closing")}
                                                                />
                                                            </div>
                                                        </div>
                                                        {errors.date_closing
                                                            ? <div className="kt-form-message">{errors.date_closing}</div>
                                                            : <div className="kt-form-description">When does this opportunity close?</div>
                                                        }
                                                    </div>
                                                    <div className="kt-form-item">
                                                        <label className="kt-form-label">Results Date</label>
                                                        <div className="kt-form-control">
                                                            <div className="kt-input">
                                                                <i className="ki-outline ki-calendar"></i>
                                                                <input id="date_results" className="grow" type="text" placeholder="Select date" data-kt-date-picker="true" data-kt-date-picker-input-mode="true" data-kt-date-picker-position-to-input="left" readOnly />
                                                            </div>
                                                        </div>
                                                        <div className="kt-form-description">When will applicants be notified?</div>
                                                    </div>
                                                </div>

                                                {/* AI Shortlisting */}
                                                {applicationType !== "external" && (
                                                    <div className="flex flex-col gap-3 pt-4 border-t border-border">
                                                        <label className="kt-form-label">Imisepela AI Shortlisting</label>
                                                        <p className="text-sm text-muted-foreground">Imisepela AI reviews applications to shortlist top candidates. Would you like to use this option?</p>
                                                        <div className="grid grid-cols-2 gap-3">
                                                            <label htmlFor="ai_yes" className="relative flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all border-border hover:border-primary hover:bg-primary/5 has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                                                                <input
                                                                    type="radio"
                                                                    id="ai_yes"
                                                                    name="ai_shortlist"
                                                                    value="yes"
                                                                    className="mt-0.5 kt-radio"
                                                                    checked={useAI}
                                                                    onChange={() => setUseAI(true)}
                                                                />
                                                                <div className="flex flex-col gap-0.5">
                                                                    <span className="text-sm font-medium text-mono">Yes, use AI Shortlisting</span>
                                                                    <span className="text-xs text-muted-foreground">Let Imisepela AI automatically shortlist the best candidates.</span>
                                                                </div>
                                                            </label>
                                                            <label htmlFor="ai_no" className="relative flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all border-border hover:border-primary hover:bg-primary/5 has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                                                                <input
                                                                    type="radio"
                                                                    id="ai_no"
                                                                    name="ai_shortlist"
                                                                    value="no"
                                                                    className="mt-0.5 kt-radio"
                                                                    checked={!useAI}
                                                                    onChange={() => setUseAI(false)}
                                                                />
                                                                <div className="flex flex-col gap-0.5">
                                                                    <span className="text-sm font-medium text-mono">No, I'll review manually</span>
                                                                    <span className="text-xs text-muted-foreground">You will manually review all applications yourself.</span>
                                                                </div>
                                                            </label>
                                                        </div>
                                                        {useAI && (
                                                            <div className="flex items-start gap-3 p-4 rounded-lg bg-primary/5 border border-blue-400">
                                                                <i className="ki-filled ki-information-2 text-primary text-lg mt-0.5"></i>
                                                                <div className="flex flex-col gap-1">
                                                                    <span className="text-sm font-medium text-mono">How AI Shortlisting works</span>
                                                                    <p className="text-xs">Once your opportunity closes, Imisepela AI will analyse all applications based on the qualifications, experience and requirements you have set. It will then rank and shortlist the top candidates, saving you time and ensuring a fair review process.</p>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {/* Privacy Policy */}
                                                <div className="kt-form-item pt-4 border-t border-border">
                                                    <div className="kt-form-control">
                                                        <label htmlFor="privacy_policy"
                                                               className={`relative flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all hover:border-primary hover:bg-primary/5 has-[:checked]:border-primary has-[:checked]:bg-primary/5 ${errors.privacy_policy ? 'border-destructive' : 'border-border'}`}
                                                               onChange={() => clearFieldError("privacy_policy")}
                                                        >
                                                            <input type="checkbox" id="privacy_policy" className="mt-0.5 kt-checkbox" />
                                                            <div className="flex flex-col gap-0.5">
                                                                <span className="text-sm font-medium text-mono">Data Privacy Policy <span className="text-destructive">*</span></span>
                                                                <span className="text-xs text-muted-foreground">I agree to the <a href="#" className="text-primary hover:underline">data privacy policy</a> and confirm that all information provided is accurate.</span>
                                                            </div>
                                                        </label>
                                                    </div>
                                                    {errors.privacy_policy && <div className="kt-form-message mt-2">{errors.privacy_policy}</div>}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* ── Footer ── */}
                                <div className="kt-card-footer justify-between p-5">
                                    <button type="button" className={`kt-btn kt-btn-secondary ${currentStep === 1 ? "invisible" : ""}`} onClick={goBack}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m12 19-7-7 7-7"></path><path d="M19 12H5"></path></svg>
                                        Back
                                    </button>
                                    {currentStep < 4 ? (
                                        <button type="button" className="kt-btn kt-btn-secondary" onClick={goNext}>
                                            Next
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg>
                                        </button>
                                    ) : (
                                        <button type="button" className="kt-btn" disabled={isSubmitting} onClick={handleSubmit}>
                                            {isSubmitting ? (
                                                <>
                                                    <svg className="animate-spin size-4 shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                                                    </svg>
                                                    Submitting...
                                                </>
                                            ) : (
                                                "Submit"
                                            )}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* Editor Modal */}
            {editorModal.open && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
                    <div className="bg-background rounded-xl border border-border w-full max-w-[900px] mx-4 shadow-lg">
                        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                            <h3 className="text-sm font-medium text-mono">{editorModal.title}</h3>
                            <button type="button" onClick={handleCloseEditor} className="text-muted-foreground hover:text-mono transition-colors">
                                <i className="ki-filled ki-cross text-lg"></i>
                            </button>
                        </div>
                        <div className="p-5">
                            <textarea
                                className="kt-input w-full kt-textarea"
                                style={{ minHeight: "500px" }}
                                placeholder={`Enter ${editorModal.title.toLowerCase()}...`}
                                value={editorValues[editorModal.id] || ""}
                                onChange={(e) => {
                                    clearFieldError(editorModal.id);
                                    setEditorValues(prev => ({ ...prev, [editorModal.id]: e.target.value }));
                                }}
                            />
                        </div>
                        <div className="flex justify-end gap-2 px-5 py-4 border-t border-border">
                            <button type="button" onClick={handleCloseEditor} className="kt-btn kt-btn-secondary">Cancel</button>
                            <button type="button" onClick={handleCloseEditor} className="kt-btn kt-btn-primary">Save</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Dashboard;
